import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessComponentEnum, AccessLevelEnum, Team } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import Trans from '@lib/components/Trans/Trans';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import useTeamQueries from 'routes/Teams/utils/useTeamQueries';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { IconName } from '@lib/components/Modal/enums';
import TeamFormPage from './TeamFormPage/TeamFormPage';
import TeamViewPage from './TeamViewPage/TeamViewPage';
import useTeamActions from './utils/useTeamActions';
import { TeamViewMode } from './enums';

const modals = [
  Modals.UserFormModal,
  Modals.UserDeleteModal,
  Modals.TeamDestroyModal,
  Modals.TeamMemberRemoveModal,
];

type Props = {
  viewMode: TeamViewMode;
};

export default function TeamContainer({ viewMode }: Props) {
  const { t } = useTranslation();

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.UsersManagement) ===
    AccessLevelEnum.Read;

  const useTeamQueriesResult = useTeamQueries({
    viewMode,
  });
  const { useUsersQueryHookResult, useTeamQueryHookResult, linkedDepartments } =
    useTeamQueriesResult;

  const useBulkSelectResult = useTableItemsBulkSelect<Team>();
  const useModalManagementResult = useModalManagement(modals, () =>
    useBulkSelectResult.handleSelectSingleTableItem(null),
  );
  const useTeamActionsResult = useTeamActions({
    viewMode,
    useTeamQueryHookResult,
    useModalManagementResult,
    useUsersQueryHookResult,
    linkedDepartments,
  });
  const {
    breadCrumbsData,
    destroyLoading,
    onConfirmedTeamDestroy,
    onViewChange,
  } = useTeamActionsResult;
  const { closeAllModals, isModalOpened } = useModalManagementResult;

  const isForm =
    viewMode === TeamViewMode.Add || viewMode === TeamViewMode.Edit;
  const isView =
    viewMode === TeamViewMode.ViewDetails ||
    viewMode === TeamViewMode.ViewMembers ||
    viewMode === TeamViewMode.ViewDepartments;
  const canUpdate = !useTeamQueryHookResult?.data?.readOnly;

  useEffect(() => {
    if (viewMode === TeamViewMode.Edit && !canUpdate) {
      onViewChange(TeamViewMode.ViewDetails);
    }
  }, [canUpdate, onViewChange, viewMode]);

  if (useTeamQueryHookResult.error) {
    return (
      <ErrorPage
        status={getStatusFromError(useTeamQueryHookResult.error)}
        description={getDescriptionFromError(useTeamQueryHookResult.error)}
      />
    );
  }

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      {isForm && (
        <TeamFormPage
          useTeamActionsResult={useTeamActionsResult}
          useModalManagementResult={useModalManagementResult}
          useTeamQueriesResult={useTeamQueriesResult}
        />
      )}
      {isView && (
        <TeamViewPage
          viewMode={viewMode}
          useTeamActionsResult={useTeamActionsResult}
          useModalManagementResult={useModalManagementResult}
          useTeamQueriesResult={useTeamQueriesResult}
          isViewOnly={isViewOnly}
        />
      )}
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="team-delete-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.TeamDestroyModal)}
        loading={destroyLoading}
        onClose={closeAllModals}
        onConfirm={onConfirmedTeamDestroy}
        useProgress
        title={t('are-you-sure')}
      />
    </>
  );
}
